import {
    AttentionLevel,
    DunningStates,
    FeatureSettingsType,
    FinancesViewModel,
    FinancesViewModelTypes,
    IconName,
    InvoiceGroupViewModel,
    InvoiceState,
    Patient,
    StatementViewModel,
} from '@pxl/models/common';
import { FeaturesEffectiveModel, InvoiceGroupModel, InvoiceModel, StatementModel, VeterinarianModel } from '@pxl/shared/generated';
import { RESOURCES } from '@pxl/shared/translation';
import { List } from 'linq-collections';
import moment from 'moment/moment';

const dunningStateIdToLevelMap: { [p: string]: number } = {
    [DunningStates.None]: 0,
    [DunningStates.Level1]: 1,
    [DunningStates.Level2]: 2,
    [DunningStates.Level3]: 3,
    [DunningStates.DeferredPayment]: 4,
};

export function dunningStateToLevel(dunningStateId: string) {
    return dunningStateIdToLevelMap[dunningStateId];
}

export interface InvoiceStateInformation {
    icon: IconName;
    titleResourceKey: string;
    attentionLevel: AttentionLevel;
}

export function invoiceStateInformation(state: InvoiceState): InvoiceStateInformation {
    switch (state) {
        case InvoiceState.CreditNote: {
            return {
                icon: 'coins-credit',
                titleResourceKey: RESOURCES.petsxl.ui.dashboard.dashboard_finances.finances.credit_note,
                attentionLevel: AttentionLevel.info,
            };
        }
        case InvoiceState.Statement: {
            return {
                icon: 'bank-account',
                titleResourceKey: RESOURCES.petsxl.ui.dashboard.dashboard_finances.finances.statement,
                attentionLevel: AttentionLevel.neutral,
            };
        }
        case InvoiceState.Invoice: {
            return {
                icon: 'invoice',
                titleResourceKey: RESOURCES.petsxl.ui.dashboard.dashboard_finances.finances.invoice,
                attentionLevel: AttentionLevel.medium,
            };
        }
        case InvoiceState.InvoicePaid: {
            return {
                icon: 'invoice-payed',
                titleResourceKey: RESOURCES.petsxl.ui.dashboard.dashboard_finances.finances.invoice_paid,
                attentionLevel: AttentionLevel.info,
            };
        }
        case InvoiceState.InvoiceDunningLevel1: {
            return {
                icon: 'dunning-1',
                titleResourceKey: RESOURCES.petsxl.ui.dashboard.dashboard_finances.finances.invoice_dunning_level_1,
                attentionLevel: AttentionLevel.urgent,
            };
        }
        case InvoiceState.InvoiceDunningLevel2: {
            return {
                icon: 'dunning-2',
                titleResourceKey: RESOURCES.petsxl.ui.dashboard.dashboard_finances.finances.invoice_dunning_level_2,
                attentionLevel: AttentionLevel.urgent,
            };
        }
        case InvoiceState.InvoiceDunningLevel3: {
            return {
                icon: 'dunning-3',
                titleResourceKey: RESOURCES.petsxl.ui.dashboard.dashboard_finances.finances.invoice_dunning_level_3,
                attentionLevel: AttentionLevel.urgent,
            };
        }
        case InvoiceState.InvoicePending: {
            return {
                icon: 'invoice-pending',
                titleResourceKey: RESOURCES.petsxl.ui.dashboard.dashboard_finances.finances.invoice_pending,
                attentionLevel: AttentionLevel.info,
            };
        }
        case InvoiceState.InvoicePartiallyPaid: {
            return {
                icon: 'invoice-partly',
                titleResourceKey: RESOURCES.petsxl.ui.dashboard.dashboard_finances.finances.invoice_partially_paid,
                attentionLevel: AttentionLevel.medium,
            };
        }
        case InvoiceState.CreditNoteCanceled: {
            return {
                icon: 'credit-cancelled',
                titleResourceKey: RESOURCES.petsxl.ui.dashboard.dashboard_finances.finances.credit_note_canceled,
                attentionLevel: AttentionLevel.neutral,
            };
        }
        case InvoiceState.InvoiceCanceled: {
            return {
                icon: 'invoice-cancelled',
                titleResourceKey: RESOURCES.petsxl.ui.dashboard.dashboard_finances.finances.invoice_canceled,
                attentionLevel: AttentionLevel.neutral,
            };
        }
    }
}

export function filterForActionableItems(items: FinancesViewModel[]): FinancesViewModel[] {
    const filtered = new List<FinancesViewModel>(items)
        .where((invoice) => invoice.invoiceView?.shouldBePaid === true && !invoice.invoiceView.anyAlreadyPaidViaPetsXL)
        .orderBy((invoice) => invoice.dateForSorting.toDate())
        .toArray();

    return filtered;
}

export function getFinanceViewModel(
    type: FinancesViewModelTypes,
    veterinarian: VeterinarianModel,
    patients: Patient[],
    invoiceGroupModel: InvoiceGroupModel | null,
    statement: StatementModel | null,
    featuresEffectiveModel: FeaturesEffectiveModel | null,
    mentionedVeterinarians?: VeterinarianModel[],
): FinancesViewModel {
    const isInvoiceOrCredit = type === FinancesViewModelTypes.Invoice || type === FinancesViewModelTypes.Credit;
    const isStatement = type === FinancesViewModelTypes.Statement;
    const effectiveFeaturesPerVet = (featuresEffectiveModel && featuresEffectiveModel.featuresPerVet[veterinarian?.id]) || [];
    const onlinePaymentFeatureAvailableForUserAndVeterinarian = effectiveFeaturesPerVet.includes(FeatureSettingsType.OnlinePayment);
    const invoiceView: InvoiceGroupViewModel | null = isInvoiceOrCredit
        ? new InvoiceGroupViewModel(invoiceGroupModel, veterinarian, onlinePaymentFeatureAvailableForUserAndVeterinarian)
        : null;
    const statementView: StatementViewModel | null = isStatement ? new StatementViewModel(statement, veterinarian) : null;
    const dateForSorting = moment(
        isInvoiceOrCredit ? invoiceGroupModel.mostRecent.invoiceDate : isStatement ? statement.dateCurrentUtc : null,
    ).utc();

    return new FinancesViewModel(type, dateForSorting, veterinarian, patients, invoiceView, statementView, mentionedVeterinarians);
}

export function getFinanceViewModelSnapShot(
    invoiceId: string,
    latestInvoiceGroupModel: InvoiceGroupModel,
    veterinarianModel: VeterinarianModel,
    effectiveFeature: FeaturesEffectiveModel,
): FinancesViewModel {
    const snapshot = latestInvoiceGroupModel.all.find((currentInvoiceModel) => currentInvoiceModel?.id === invoiceId);

    const allSnapshot: InvoiceModel[] = latestInvoiceGroupModel.all.filter(
        (currentInvoiceModel) => currentInvoiceModel.createdUtc <= snapshot.createdUtc,
    );

    const snapshotGroupModel: InvoiceGroupModel = {
        mostRecent: snapshot,
        all: allSnapshot,
    };

    return getFinanceViewModel(FinancesViewModelTypes.Invoice, veterinarianModel, [], snapshotGroupModel, null, effectiveFeature);
}
